import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { LayoutGroup, motion, useReducedMotion } from 'motion/react';
import { Menu, Search } from 'lucide-react';
import { Owl } from '@/landing/Owl';
import { Dialog, DialogContent } from '@/components/arc/dialog/dialog';
import { Drawer, DrawerContent } from '@/components/arc/drawer/drawer';
import { Button } from '@/components/arc/button/button';
import { UserMenu } from '@/components/arc/user-menu/user-menu';
import { CommandPalette, type CommandItem } from '@/components/arc/command-palette/command-palette';
import { motionTokens } from '@/components/arc/lib/motion-tokens';
import { SCENEWISE_NAV, type NavItem } from '@/module/nav';
import { useHref } from '@/module/basePath';
import { setThemePreference, useThemePreference } from '@/lib/theme';
import { JobsIndicator } from '@/components/jobs/JobsDrawer';
import styles from './shell.module.css';

export interface ShellUser {
  name: string;
  email: string;
  plan?: string;
}

export interface AppShellProps {
  user: ShellUser;
  /** Returned promise keeps the sign-out item in its pending state. */
  onSignOut?: () => void | Promise<unknown>;
  /** Extra commands merged into the command palette (page actions, recent items). */
  commands?: CommandItem[];
  children: ReactNode;
}

const GROUP_ORDER: NavItem['group'][] = ['Library', 'Audiences', 'Activation', 'Account'];

/**
 * App shell: left navigation, top bar with command palette and account menu, page outlet.
 * Below 900px the navigation lives in a drawer behind the menu button.
 */
export function AppShell({ user, onSignOut, commands = [], children }: AppShellProps) {
  const href = useHref();
  const navigate = useNavigate();
  const location = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const theme = useThemePreference();
  const reduce = useReducedMotion();

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const items = useMemo<CommandItem[]>(
    () => [
      ...SCENEWISE_NAV.map((n) => ({
        id: `nav:${n.path}`,
        label: n.label,
        group: 'Go to',
        keywords: n.keywords,
        icon: <n.icon size={16} strokeWidth={1.75} />,
      })),
      { id: 'theme:light', label: 'Use light theme', group: 'Appearance', keywords: ['theme'] },
      { id: 'theme:dark', label: 'Use dark theme', group: 'Appearance', keywords: ['theme'] },
      { id: 'theme:system', label: 'Follow system theme', group: 'Appearance', keywords: ['theme'] },
      ...commands,
    ],
    [commands],
  );

  const runCommand = useCallback(
    (item: CommandItem) => {
      setPaletteOpen(false);
      if (item.id.startsWith('nav:')) navigate(href(item.id.slice(4)));
      else if (item.id.startsWith('theme:'))
        setThemePreference(item.id.slice(6) as 'light' | 'dark' | 'system');
    },
    [navigate, href],
  );

  const nav = (
    <LayoutGroup id="shell-nav">
      <nav className={styles.nav} aria-label="Main">
        {GROUP_ORDER.map((group) => (
          <div key={group} role="group" aria-label={group}>
            <div className={styles.navGroup}>{group}</div>
            {SCENEWISE_NAV.filter((n) => n.group === group).map((n) => (
              <NavLink
                key={n.path}
                to={href(n.path)}
                className={styles.navLink}
                end={n.path === '/overlay' ? false : undefined}
              >
                {({ isActive }) => (
                  <>
                    {isActive ? (
                      <motion.span
                        layoutId="shell-nav-highlight"
                        className={styles.navHighlight}
                        transition={reduce ? { duration: 0 } : motionTokens.spring.morph}
                        aria-hidden
                      />
                    ) : null}
                    <n.icon size={18} strokeWidth={1.75} className={styles.navIcon} />
                    <span>{n.label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
    </LayoutGroup>
  );

  const brand = (
    <div className={styles.brand}>
      <span className={styles.brandMark} aria-hidden>
        <Owl className={styles.owl} />
      </span>
      <span>
        <span className={styles.brandName}>Scenewise</span>
        <span className={styles.brandSub}>by Skysection</span>
      </span>
    </div>
  );

  return (
    <div className={styles.shell}>
      <aside className={styles.sidebar}>
        {brand}
        {nav}
        <div className={styles.sidebarFooter}>
          <span>Every scene, understood.</span>
        </div>
      </aside>

      <div className={styles.main}>
        <header className={styles.topbar}>
          <div className={styles.topbarLeft}>
            <span className={styles.menuButton}>
              <Button variant="ghost" size="sm" aria-label="Open navigation" onClick={() => setNavOpen(true)}>
                <Menu size={18} strokeWidth={1.75} />
              </Button>
            </span>
            <button
              type="button"
              className={styles.searchButton}
              onClick={() => setPaletteOpen(true)}
              aria-keyshortcuts="Control+K Meta+K"
            >
              <Search size={16} strokeWidth={1.75} />
              <span className={styles.searchLabel}>Search or jump to</span>
              <span className={styles.searchKbd} aria-hidden>
                <kbd>⌘</kbd>
                <kbd>K</kbd>
              </span>
            </button>
          </div>
          <div className={styles.topbarRight}>
            <JobsIndicator />
            <UserMenu
              user={user}
              theme={theme}
              onThemeChange={setThemePreference}
              showTheme
              onSignOut={onSignOut}
            />
          </div>
        </header>
        <div className={styles.content}>{children}</div>
      </div>

      <Dialog open={paletteOpen} onOpenChange={setPaletteOpen}>
        <DialogContent title="Search" description="Jump to a page or run a command">
          <div className={styles.paletteBody}>
            <CommandPalette
              items={items}
              placeholder="Search pages and commands"
              onSelect={runCommand}
              onClose={() => setPaletteOpen(false)}
              autoFocus
            />
          </div>
        </DialogContent>
      </Dialog>

      <Drawer open={navOpen} onOpenChange={setNavOpen}>
        <DrawerContent title="Navigation" side="left">
          {brand}
          {nav}
        </DrawerContent>
      </Drawer>
    </div>
  );
}
